import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Box, Button, Tabs, useMediaQuery, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import AvatarInfoCard from '@ui/components/common/AvatarInfoCard';
import TabWithBadge from '@ui/components/TabWithBadge';
import { Add, Bus, Location, Speedometer, User, Export } from 'iconsax-react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import VehicleTab from './VehicleTab';
import NewVehicleDialog from './NewVehicleDialog';
import LocationAssignedAttendeeTab from './LocationAssignedAttendeeTab';
import LocationUnassignedAttendeeTab from './LocationUnassignedAttendeeTab';
import LocationAttendeesTab from './LocationAttendeesTab';
import SearchFilter from '@ui/components/Button/SearchFilter';
import ReassignVehicleDialog from './ReassignVehicleDialog';
import ReassignPickupLocationDialog from './ReassignPickupLocationDialog';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getPickupLocationByIdAsync } from '@application/features/pickupLocation/pickupLocationThunk';
import { resetAttendees } from '@application/features/attendeePickupAssignment/attendeePickupAssignmentSlice';
import { getAssignedFamilyFestAttendeesByPickupLocationIdAsync, getUnassignedFamilyFestAttendeesByPickupLocationIdAsync, reassignFamilyFestAttendeesToPickupLocationAsync, autoAssignFamilyFestAttendeesToPickupLocationAsync } from '@application/features/familyFest/familyFestPickupAssignmentThunk';
import { resetFamilyFestAttendees } from '@application/features/familyFest/familyFestPickupAssignmentSlice';
import { CollaboratorAccessLevel } from '@application/enums/collaborator-access-level';
import { EventTypeEnum } from '@application/enums/event-type-enum';
import { getAssignedAttendeesByPickupLocationIdAsync, getUnassignedAttendeesByPickupLocationIdAsync, reassignAttendeesToPickupLocationAsync } from '@application/features/attendeePickupAssignment/attendeePickupAssignmentThunk';
import { getFamilyFestPickupLocationByIdAsync, downloadFamilyFestPickupLocationAttendeesExcelAsync } from '@application/features/familyFest/familyFestPickupLocationThunk';
import { getVDKEventByIdForEventDetailsAsync } from '@application/features/vdkEvent/VDKEventThunk';
import { EventDocumentType } from '@application/enums/event-document-type';
const LocationDetail = ({ locationId }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const { t } = useTranslation();
    const { eventId } = useParams();
    const dispatch = useAppDispatch();
    const { assignedAttendees: standardAssignedAttendees, unassignedAttendees: standardUnassignedAttendees } = useAppSelector((state) => state.attendeePickupAssignment);
    const { assignedAttendees: familyFestAssignedAttendees, unassignedAttendees: familyFestUnassignedAttendees } = useAppSelector((state) => state.familyFestPickupAssignment);
    const { attendees: familyFestAttendees } = useAppSelector((state) => state.familyFestPickupAssignment);
    const { familyFestPickupLocation } = useAppSelector((state) => state.familyFestPickupLocation);
    const { pickupLocation } = useAppSelector((state) => state.pickupLocation);
    const { VDKEventsForEventDetails } = useAppSelector((state) => state.VDKEvent);
    const isFamilyFest = VDKEventsForEventDetails?.type === EventTypeEnum.Family;
    const assignedAttendees = isFamilyFest ? familyFestAssignedAttendees : standardAssignedAttendees;
    const unassignedAttendees = isFamilyFest
        ? familyFestUnassignedAttendees
        : standardUnassignedAttendees;
    const pickupLocationdetails = isFamilyFest ? familyFestPickupLocation : pickupLocation;
    const [activeTab, setActiveTab] = useState('assigned');
    // FamilyFest locations use a single consolidated attendee tab instead of assigned/unassigned.
    useEffect(() => {
        setActiveTab(isFamilyFest ? 'attendees' : 'assigned');
    }, [isFamilyFest]);
    const prevLocationIdRef = useRef(undefined);
    const [openNewVehicle, setOpenNewVehicle] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [selectedUnassignedIds, setSelectedUnassignedIds] = useState([]);
    const [vehicleFilterOpen, setVehicleFilterOpen] = useState(false);
    const [assignedFilterOpen, setAssignedFilterOpen] = useState(false);
    const [unassignedFilterOpen, setUnassignedFilterOpen] = useState(false);
    const [attendeesFilterOpen, setAttendeesFilterOpen] = useState(false);
    const [attendeesSelectedRows, setAttendeesSelectedRows] = useState([]);
    const [bulkAssignVehicleOpen, setBulkAssignVehicleOpen] = useState(false);
    const [assignVehicleDialogOpen, setAssignVehicleDialogOpen] = useState(false);
    const [reassignPickupDialogOpen, setReassignPickupDialogOpen] = useState(false);
    const [isExportingAttendees, setIsExportingAttendees] = useState(false);
    const [isAutoAssigning, setIsAutoAssigning] = useState(false);
    const [attendeesRefreshKey, setAttendeesRefreshKey] = useState(0);
    const [attendeesHasFilters, setAttendeesHasFilters] = useState(false);
    const [attendeesResetFiltersKey, setAttendeesResetFiltersKey] = useState(0);
    const navigate = useNavigate();
    const currentUser = useAppSelector((state) => state.auth.currentUser);
    const isTransportManager = Array.isArray(currentUser?.role) && currentUser?.role.includes('TransportManager');
    const isHappinessManager = Array.isArray(currentUser?.role) && currentUser?.role.includes('HappinessManager');
    const isExternalTransportManager = isTransportManager &&
        Number(currentUser?.CollaboratorAccessLevel) === CollaboratorAccessLevel.External;
    const restrictedTabsForExternalTransportManager = isFamilyFest
        ? ['attendees']
        : ['assigned', 'unassigned'];
    const baseTabs = isFamilyFest
        ? [
            {
                labelText: t('events.eventDetails.transportTab.attendees'),
                value: 'attendees',
                badgeCount: familyFestAttendees?.totalCount ??
                    (pickupLocationdetails?.assignedAttendeesCount ?? 0) +
                        (pickupLocationdetails?.unassignedAttendeeCount ?? 0)
            }
        ]
        : [
            { labelText: t('events.eventDetails.transportTab.vehicleList'), value: 'vehicles' },
            {
                labelText: t('events.eventDetails.transportTab.assignedAttendees'),
                value: 'assigned',
                badgeCount: assignedAttendees?.totalCount ?? pickupLocationdetails?.assignedAttendeesCount ?? 0
            },
            {
                labelText: t('events.eventDetails.transportTab.unassignedAttendees'),
                value: 'unassigned',
                badgeCount: unassignedAttendees?.totalCount ?? pickupLocationdetails?.unassignedAttendeeCount ?? 0
            }
        ];
    const tabLabels = baseTabs.filter((tab) => {
        if (isExternalTransportManager) {
            return !restrictedTabsForExternalTransportManager.includes(tab.value);
        }
        return true;
    });
    const handlePickupLocationTabChange = (_, value) => {
        setActiveTab(value);
    };
    const handleExportAttendeesExcel = async () => {
        if (!locationId)
            return;
        try {
            setIsExportingAttendees(true);
            await dispatch(downloadFamilyFestPickupLocationAttendeesExcelAsync({ pickupLocationId: String(locationId) })).unwrap();
            toast.success(t('events.eventDetails.transportTab.pickupLocationAttendeesExportedSuccessfully'));
        }
        catch (error) {
            toast.error(error?.error || error?.message || t('events.eventDetails.transportTab.pickupLocationAttendeesExportFailed'));
        }
        finally {
            setIsExportingAttendees(false);
        }
    };
    const handleAutoAssignAttendees = async () => {
        if (!locationId)
            return;
        try {
            setIsAutoAssigning(true);
            const result = await dispatch(autoAssignFamilyFestAttendeesToPickupLocationAsync(String(locationId))).unwrap();
            if (result.totalUnassignedBeforeCount === 0) {
                toast.success(t('events.eventDetails.transportTab.autoAssignNothingToAssign'));
            }
            else {
                const remaining = result.totalUnassignedBeforeCount - result.assignedCount;
                toast.success(t('events.eventDetails.transportTab.autoAssignAttendeesSuccessfully', {
                    assigned: result.assignedCount,
                    total: result.totalUnassignedBeforeCount,
                    remaining
                }));
            }
            dispatch(getFamilyFestPickupLocationByIdAsync(String(locationId)));
            setAttendeesRefreshKey((key) => key + 1);
        }
        catch (error) {
            toast.error(error?.error || error?.message || t('events.eventDetails.transportTab.autoAssignAttendeesFailed'));
        }
        finally {
            setIsAutoAssigning(false);
        }
    };
    const formatPickupTime = (timeOfPickup) => {
        if (!timeOfPickup)
            return '—';
        const value = String(timeOfPickup).trim();
        const match = /T(\d{2}):(\d{2})/.exec(value) ?? /^(\d{1,2}):(\d{2})/.exec(value);
        if (!match)
            return '—';
        return `${match[1].padStart(2, '0')}:${match[2]}`;
    };
    // Clear unassigned selection when leaving the unassigned tab
    useEffect(() => {
        if (activeTab !== 'unassigned' && selectedUnassignedIds.length > 0) {
            setSelectedUnassignedIds([]);
        }
    }, [activeTab, selectedUnassignedIds.length]);
    // External transport managers may only view locations where SupplierId matches their Collaborator id (not User id).
    useEffect(() => {
        if (!pickupLocation || !isExternalTransportManager || !eventId)
            return;
        const collaboratorId = currentUser?.CollaboratorId !== undefined && currentUser?.CollaboratorId !== null
            ? String(currentUser.CollaboratorId)
            : '';
        const supplierId = pickupLocation.supplierId !== undefined && pickupLocation.supplierId !== null
            ? String(pickupLocation.supplierId)
            : '';
        if (!collaboratorId || !supplierId || collaboratorId !== supplierId) {
            navigate(`/event-details/${eventId}?tab=transport`, { replace: true });
        }
    }, [pickupLocation, isExternalTransportManager, currentUser?.CollaboratorId, eventId, navigate]);
    //Refresh the Event details
    useEffect(() => {
        if (!eventId || VDKEventsForEventDetails?.id === eventId) {
            return;
        }
        dispatch(getVDKEventByIdForEventDetailsAsync({
            id: eventId,
            documentType: EventDocumentType.BackgroundImage
        }));
    }, [dispatch, eventId, VDKEventsForEventDetails?.id]);
    // Fetch pickup location details; reset attendees only when locationId actually changes
    useEffect(() => {
        if (!locationId)
            return;
        const locationChanged = prevLocationIdRef.current !== locationId;
        prevLocationIdRef.current = locationId;
        if (locationChanged) {
            dispatch(resetFamilyFestAttendees());
            dispatch(resetAttendees());
        }
        if (isFamilyFest) {
            dispatch(getFamilyFestPickupLocationByIdAsync(String(locationId)))
                .unwrap()
                .catch(() => {
                if (eventId)
                    navigate(`/event-details/${eventId}?tab=transport`, { replace: true });
            });
        }
        else {
            dispatch(getPickupLocationByIdAsync(String(locationId)))
                .unwrap()
                .catch(() => {
                if (eventId)
                    navigate(`/event-details/${eventId}?tab=transport`, { replace: true });
            });
        }
    }, [dispatch, locationId, eventId, navigate, isFamilyFest]);
    return (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: '20px', width: '100%', height: 'calc(100dvh - 127px)', sx: { overflowY: 'auto', pr: 1 }, children: [_jsxs(Grid, { container: true, spacing: '12px', gap: '12px', sx: { width: '100%' }, children: [_jsx(Grid, { size: { xs: 12, md: 4 }, children: _jsx(AvatarInfoCard, { header: t('events.eventDetails.transportTab.pickupLocationName'), labelIcon: _jsx(Location, { variant: 'Outline' }), subheader: pickupLocationdetails?.name || '—' }) }), _jsx(Grid, { size: { xs: 12, md: 4 }, children: _jsx(AvatarInfoCard, { header: t('events.eventDetails.transportTab.pickupLocationAddress'), labelIcon: _jsx(Icon, { icon: 'hugeicons:building-02' }), subheader: pickupLocationdetails?.address || '—' }) }), !isFamilyFest && (_jsx(Grid, { size: { xs: 12, md: 4 }, children: _jsx(AvatarInfoCard, { header: t('events.eventDetails.transportTab.totalSeats'), labelIcon: _jsx(Speedometer, { variant: 'Outline' }), subheader: pickupLocationdetails?.totalCapacity !== undefined &&
                                pickupLocationdetails?.assignedAttendees !== undefined
                                ? `${pickupLocationdetails.assignedAttendees}/${pickupLocationdetails.totalCapacity}`
                                : '—' }) })), _jsx(Grid, { size: { xs: 12, md: 4 }, children: _jsx(AvatarInfoCard, { header: t('events.eventDetails.transportTab.timeOfPickup'), labelIcon: _jsx(Icon, { icon: 'hugeicons:clock-01' }), subheader: formatPickupTime(pickupLocationdetails?.timeOfPickup) }) }), !isTransportManager && (_jsx(Grid, { size: { xs: 12, md: 4 }, children: _jsx(AvatarInfoCard, { header: t('events.eventDetails.transportTab.totalCompanies'), labelIcon: _jsx(Icon, { icon: 'hugeicons:building-06' }), subheader: pickupLocationdetails?.eventCompanies?.length?.toString() || '0' }) })), !isFamilyFest && (_jsx(Grid, { size: { xs: 12, md: 4 }, children: _jsx(AvatarInfoCard, { header: t('events.eventDetails.transportTab.suppliers'), labelIcon: _jsx(User, { variant: 'Outline' }), subheader: pickupLocationdetails?.supplierName || '—' }) })), isTransportManager ? (_jsxs(_Fragment, { children: [_jsx(Grid, { size: { xs: 12, md: 4 }, children: _jsx(AvatarInfoCard, { header: t('events.eventDetails.transportTab.assignedAttendees'), labelIcon: _jsx(Bus, { variant: 'Outline' }), subheader: pickupLocationdetails?.assignedAttendeesCount?.toString() || '0' }) }), _jsx(Grid, { size: { xs: 12, md: 4 }, children: _jsx(AvatarInfoCard, { header: t('events.eventDetails.transportTab.unassignedAttendees'), labelIcon: _jsx(Bus, { variant: 'Outline' }), subheader: pickupLocationdetails?.unassignedAttendeeCount?.toString() || '0' }) })] })) : (!isFamilyFest && (_jsx(Grid, { size: { xs: 12, md: 4 }, children: _jsx(AvatarInfoCard, { header: t('events.eventDetails.transportTab.vehicles'), labelIcon: _jsx(Bus, { variant: 'Outline' }), subheader: pickupLocationdetails?.vehicleCount?.toString() || '0' }) })))] }), _jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: isSmallScreen ? undefined : 'center', flexDirection: isSmallScreen ? 'column' : 'row', gap: isSmallScreen ? '12px' : 0, children: [_jsx(Box, { children: _jsx(Tabs, { value: activeTab, onChange: handlePickupLocationTabChange, variant: 'scrollable', scrollButtons: 'auto', sx: {
                                '& .MuiTab-root:not(.Mui-selected)': {
                                    color: theme.palette.text.primary
                                },
                                '& .MuiTab-root.Mui-selected': {
                                    color: theme.palette.text.primary
                                }
                            }, children: tabLabels.map((tab) => (_jsx(TabWithBadge, { labelText: tab.labelText, value: tab.value, badgeCount: tab.badgeCount }, tab.value))) }) }), _jsxs(Box, { display: 'flex', gap: isSmallScreen ? '12px' : 0.5, alignItems: isSmallScreen ? undefined : 'center', flexDirection: isSmallScreen ? 'column-reverse' : 'row', children: [_jsx(Box, { flexShrink: 0, children: _jsx(SearchFilter, { forceDesktopVariant: isSmallScreen, fullWidth: isSmallScreen, searchValue: searchValue, onSearchChange: (value) => setSearchValue(value), placeholder: t('placeholders.search'), onClear: () => setSearchValue(''), widthBox: '200px', bgVariant: 'white', forceIconVariant: true, isDatagrid: true, iconButtonSize: 'medium', heightBox: '42px', onFilterClick: () => {
                                        if (activeTab === 'vehicles')
                                            setVehicleFilterOpen(true);
                                        else if (activeTab === 'assigned')
                                            setAssignedFilterOpen(true);
                                        else if (activeTab === 'unassigned')
                                            setUnassignedFilterOpen(true);
                                        else if (activeTab === 'attendees')
                                            setAttendeesFilterOpen(true);
                                    }, filterLabel: t('users.filters'), showFilterClearButton: activeTab === 'attendees' && attendeesHasFilters, handleFilterClear: () => {
                                        setAttendeesHasFilters(false);
                                        setAttendeesResetFiltersKey((key) => key + 1);
                                    } }) }), isFamilyFest && activeTab === 'attendees' && attendeesSelectedRows.length > 0 && (_jsx(Button, { fullWidth: isSmallScreen, variant: 'outlined', color: 'primary', size: isSmallScreen ? 'small' : 'medium', onClick: () => setBulkAssignVehicleOpen(true), children: t('events.eventDetails.transportTab.assignVehicle') })), isFamilyFest && isHappinessManager && activeTab === 'attendees' && (_jsx(Button, { fullWidth: isSmallScreen, variant: 'outlined', color: 'primary', size: isSmallScreen ? 'small' : 'medium', startIcon: _jsx(Bus, { variant: 'Outline' }), disabled: isAutoAssigning, onClick: handleAutoAssignAttendees, children: t('events.eventDetails.transportTab.autoAssignAttendees') })), isFamilyFest && activeTab === 'attendees' && (_jsx(Button, { fullWidth: isSmallScreen, variant: 'outlined', color: 'primary', size: isSmallScreen ? 'small' : 'medium', startIcon: _jsx(Export, { variant: 'Outline' }), disabled: isExportingAttendees, onClick: handleExportAttendeesExcel, children: t('common.exportToExcel', 'Export to Excel') })), activeTab === 'vehicles' && !isFamilyFest && (_jsx(Button, { fullWidth: isSmallScreen, variant: 'outlined', color: 'primary', size: isSmallScreen ? 'small' : 'medium', startIcon: _jsx(Add, { variant: 'Outline' }), disabled: pickupLocationdetails?.isClosed, onClick: () => setOpenNewVehicle(true), children: t('events.eventDetails.transportTab.addVehicle') })), activeTab === 'unassigned' && selectedUnassignedIds.length > 0 && (_jsxs(_Fragment, { children: [_jsx(Button, { variant: 'outlined', color: 'primary', startIcon: _jsx(Bus, { variant: 'Outline' }), size: isSmallScreen ? 'small' : 'medium', disabled: pickupLocationdetails?.isClosed, onClick: () => setAssignVehicleDialogOpen(true), children: t('events.eventDetails.transportTab.assignVehicle') }), !isFamilyFest && (_jsx(Button, { variant: 'outlined', color: 'primary', startIcon: _jsx(Location, { variant: 'Outline' }), size: isSmallScreen ? 'small' : 'medium', disabled: pickupLocationdetails?.isClosed, onClick: () => setReassignPickupDialogOpen(true), children: t('events.eventDetails.transportTab.reassignPickupLocation') }))] }))] })] }), _jsxs(Box, { children: [!isFamilyFest && activeTab === 'vehicles' && (_jsx(VehicleTab, { filterOpen: vehicleFilterOpen, onFilterOpenChange: setVehicleFilterOpen, searchValue: searchValue, locationId: locationId, isFamilyFest: isFamilyFest })), !isFamilyFest && !isExternalTransportManager && activeTab === 'assigned' && (_jsx(LocationAssignedAttendeeTab, { filterOpen: assignedFilterOpen, onFilterOpenChange: setAssignedFilterOpen, searchValue: searchValue, locationId: locationId, isFamilyFest: isFamilyFest })), !isFamilyFest && !isExternalTransportManager && activeTab === 'unassigned' && (_jsx(LocationUnassignedAttendeeTab, { selectedRows: selectedUnassignedIds, onSelectionChange: (rows) => setSelectedUnassignedIds(rows), filterOpen: unassignedFilterOpen, onFilterOpenChange: setUnassignedFilterOpen, searchValue: searchValue, locationId: locationId, isFamilyFest: isFamilyFest })), isFamilyFest && !isExternalTransportManager && activeTab === 'attendees' && (_jsx(LocationAttendeesTab, { filterOpen: attendeesFilterOpen, onFilterOpenChange: setAttendeesFilterOpen, searchValue: searchValue, locationId: locationId, selectedRows: attendeesSelectedRows, onSelectionChange: setAttendeesSelectedRows, bulkAssignVehicleOpen: bulkAssignVehicleOpen, onBulkAssignVehicleOpenChange: setBulkAssignVehicleOpen, refreshKey: attendeesRefreshKey, resetFiltersKey: attendeesResetFiltersKey, onFiltersChange: (filters) => setAttendeesHasFilters(Object.keys(filters).length > 0) }))] }), _jsx(NewVehicleDialog, { open: openNewVehicle, onClose: () => setOpenNewVehicle(false), locationId: locationId, isFamilyFest: isFamilyFest, onSuccess: () => {
                    // Refresh pickup location details after vehicle creation/update
                    if (locationId) {
                        if (isFamilyFest) {
                            dispatch(getFamilyFestPickupLocationByIdAsync(String(locationId)));
                        }
                        else {
                            dispatch(getPickupLocationByIdAsync(String(locationId)));
                        }
                    }
                } }), _jsx(ReassignVehicleDialog, { open: assignVehicleDialogOpen, onClose: () => {
                    setAssignVehicleDialogOpen(false);
                    setSelectedUnassignedIds([]);
                }, title: t('events.eventDetails.transportTab.assignVehicle'), attendeeIds: selectedUnassignedIds.length > 0 ? selectedUnassignedIds : undefined, locationId: locationId, isFamilyFest: isFamilyFest, onSuccess: () => {
                    // Refresh the unassigned and assigned attendees lists and location summary counts
                    if (locationId) {
                        if (isFamilyFest) {
                            dispatch(getFamilyFestPickupLocationByIdAsync(String(locationId)));
                            dispatch(getUnassignedFamilyFestAttendeesByPickupLocationIdAsync({
                                pickupLocationId: locationId,
                                pageNumber: 1,
                                pageSize: 10,
                                searchString: searchValue || undefined
                            }));
                            dispatch(getAssignedFamilyFestAttendeesByPickupLocationIdAsync({
                                pickupLocationId: locationId,
                                pageNumber: 1,
                                pageSize: 10,
                                searchString: searchValue || undefined
                            }));
                        }
                        else {
                            dispatch(getPickupLocationByIdAsync(String(locationId)));
                            dispatch(getUnassignedAttendeesByPickupLocationIdAsync({
                                pickupLocationId: locationId,
                                pageNumber: 1,
                                pageSize: 10,
                                searchString: searchValue || undefined
                            }));
                            dispatch(getAssignedAttendeesByPickupLocationIdAsync({
                                pickupLocationId: locationId,
                                pageNumber: 1,
                                pageSize: 10,
                                searchString: searchValue || undefined
                            }));
                        }
                    }
                } }), _jsx(ReassignPickupLocationDialog, { open: reassignPickupDialogOpen, pickupLocationId: locationId, isFamilyFest: isFamilyFest, onClose: () => {
                    setReassignPickupDialogOpen(false);
                    setSelectedUnassignedIds([]);
                }, onConfirm: async (location) => {
                    if (selectedUnassignedIds.length > 0) {
                        try {
                            const attendeeIds = selectedUnassignedIds.map((id) => String(id));
                            let response;
                            if (isFamilyFest) {
                                response = await dispatch(reassignFamilyFestAttendeesToPickupLocationAsync({
                                    attendeeIds: attendeeIds,
                                    pickupLocationId: String(location.id)
                                })).unwrap();
                            }
                            else {
                                response = await dispatch(reassignAttendeesToPickupLocationAsync({
                                    attendeeIds: attendeeIds,
                                    pickupLocationId: String(location.id)
                                })).unwrap();
                            }
                            if (response.isSuccess) {
                                const isBulk = attendeeIds.length > 1;
                                toast.success(response.message ||
                                    (isBulk
                                        ? t('events.eventDetails.transportTab.attendeesReassignedToPickupLocationSuccessfully', { count: attendeeIds.length })
                                        : t('events.eventDetails.transportTab.attendeeReassignedToPickupLocationSuccessfully')));
                                // Refresh the lists and location summary counts
                                if (locationId) {
                                    if (isFamilyFest) {
                                        dispatch(getFamilyFestPickupLocationByIdAsync(String(locationId)));
                                        dispatch(getAssignedFamilyFestAttendeesByPickupLocationIdAsync({
                                            pickupLocationId: locationId,
                                            pageNumber: 1,
                                            pageSize: 10,
                                            searchString: searchValue || undefined
                                        }));
                                        dispatch(getUnassignedFamilyFestAttendeesByPickupLocationIdAsync({
                                            pickupLocationId: locationId,
                                            pageNumber: 1,
                                            pageSize: 10,
                                            searchString: searchValue || undefined
                                        }));
                                    }
                                    else {
                                        dispatch(getPickupLocationByIdAsync(String(locationId)));
                                        dispatch(getAssignedAttendeesByPickupLocationIdAsync({
                                            pickupLocationId: locationId,
                                            pageNumber: 1,
                                            pageSize: 10,
                                            searchString: searchValue || undefined
                                        }));
                                        dispatch(getUnassignedAttendeesByPickupLocationIdAsync({
                                            pickupLocationId: locationId,
                                            pageNumber: 1,
                                            pageSize: 10,
                                            searchString: searchValue || undefined
                                        }));
                                    }
                                }
                                setSelectedUnassignedIds([]);
                                setReassignPickupDialogOpen(false);
                            }
                            else {
                                const isBulk = attendeeIds.length > 1;
                                toast.error(response.message ||
                                    (isBulk
                                        ? t('events.eventDetails.transportTab.attendeesReassignToPickupLocationFailed')
                                        : t('events.eventDetails.transportTab.attendeeReassignToPickupLocationFailed')));
                            }
                        }
                        catch (error) {
                            const isBulk = selectedUnassignedIds.length > 1;
                            const errorMessage = error?.message ||
                                (isBulk
                                    ? t('events.eventDetails.transportTab.attendeesReassignToPickupLocationFailed')
                                    : t('events.eventDetails.transportTab.attendeeReassignToPickupLocationFailed'));
                            toast.error(errorMessage);
                        }
                    }
                } })] }));
};
export default LocationDetail;
